import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  FiCalendar, 
  FiCoffee, 
  FiShoppingCart, 
  FiFileText, 
  FiLogOut,
  FiMenu,
  FiX,
  FiActivity,
  FiUser,
  FiSettings,
  FiTag  // Added for promotions
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [activeHover, setActiveHover] = useState(null);
  const [time, setTime] = useState(new Date());
  
  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const isActive = (path) => location.pathname.includes(path);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  return (
    <div className="dashboard-container">
      {/* Animated sidebar with toggle */}
      <motion.nav 
        className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''}`}
        initial={{ width: 280 }}
        animate={{ width: collapsed ? 80 : 280 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >

        {/* Time/Date Widget */}
        {!collapsed && (
          <motion.div 
            className="time-widget"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="time">{time.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
            <div className="date">{time.toLocaleDateString([], {weekday: 'short', month: 'short', day: 'numeric'})}</div>
          </motion.div>
        )}

        <ul className="nav-list">
          <NavItem 
            to="/dashboard/reservations" 
            icon={<FiCalendar size={20} />}
            active={isActive('reservations')}
            collapsed={collapsed}
            hoverState={[activeHover, setActiveHover]}
            index={0}
          >
            Reservierungen
          </NavItem>
          
          <NavItem 
            to="/dashboard/menu" 
            icon={<FiCoffee size={20} />}
            active={isActive('menu')}
            collapsed={collapsed}
            hoverState={[activeHover, setActiveHover]}
            index={1}
          >
            Menü verwalten
          </NavItem>
          
          <NavItem 
            to="/dashboard/orders" 
            icon={<FiShoppingCart size={20} />}
            active={isActive('orders')}
            collapsed={collapsed}
            hoverState={[activeHover, setActiveHover]}
            index={2}
          >
            Bestellungen
          </NavItem>

          {/* ✅ NEW: Table Management NavItem */}
          <NavItem 
            to="/dashboard/tables" 
            icon={<FiActivity size={20} />}
            active={isActive('tables')}
            collapsed={collapsed}
            hoverState={[activeHover, setActiveHover]}
            index={3}
          >
            Tischverwaltung
          </NavItem>

          <NavItem 
            to="/dashboard/promotions" 
            icon={<FiTag size={20} />}
            active={isActive('promotions')}
            collapsed={collapsed}
            hoverState={[activeHover, setActiveHover]}
            index={4}
          >
           Werbeaktionen
          </NavItem>
        </ul>

        {/* Dynamic accent that follows hover */}
        <motion.div 
          className="accent"
          animate={{
            y: activeHover !== null ? activeHover * 56 + 16 : 0,
            opacity: activeHover !== null ? 0.2 : 0
          }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
        
        <div className="footer">
          {!collapsed && (
            <motion.div 
              className="user-panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="user-avatar">
                <FiUser size={18} />
              </div>
              <div className="user-info">
                <div className="user-name">Admin-Benutzer</div>
                <div className="user-role">Super Admin</div>
              </div>
            </motion.div>
          )}
          
          <button onClick={handleLogout} className="logout-link">
            <FiLogOut size={18} />
            {!collapsed && <span>Abmelden</span>}
          </button>
        </div>
      </motion.nav>

      <main className="main-content">
        {/* Animated content area */}
        <AnimatePresence mode='wait'>
          <motion.div
            key={location.pathname}
            className="content-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

const NavItem = ({ to, icon, children, active, collapsed, hoverState, index }) => {
  const [hover, setHover] = useState(false);
  const [activeHover, setActiveHover] = hoverState;

  return (
    <li 
      className="nav-item"
      onMouseEnter={() => {
        setHover(true);
        setActiveHover(index);
      }}
      onMouseLeave={() => {
        setHover(false);
        setActiveHover(null);
      }}
    >
      <Link 
        to={to} 
        className={`nav-link ${active ? 'active-link' : ''} ${hover ? 'hover-link' : ''}`}
      >
        <span className="nav-icon">{icon}</span>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {children}
          </motion.span>
        )}
        {active && !collapsed && (
          <motion.div 
            className="active-indicator"
            layoutId="activeIndicator"
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          />
        )}
      </Link>
    </li>
  );
};

export default AdminDashboard;
